<template>
  <block-item-card
    :min-height="minBlockHeight"
    :title="$t('ALM.Section.InvestmentsPortfolio.InvestmentsAllocation.Title')"
  >
    <v-row class="ma-0">
      <!-- Left Section - Donut Chart -->
      <v-col
        class="ma-0 pa-2"
        cols="12"
        md="3"
      >
        <div class="text-center">
          <!-- Donut Chart -->
          <v-lazy
            :options="{threshold: .5}"
            transition="fade-transition"
          >
            <base-e-chart-pie
              :chart-data-options="investmentsAllocation"
              :chart-height="200"
              :pie-radius="['40%', '70%']"
              :tooltip-show="true"
              chart-animation-easing="elasticOut"
              tooltip-confine
              :label-line-show="false"
              :label-show="false"
              :legend-show="false"
              :default-colors="pieColors"
              :tooltip-prefix="switchValue ? currencySymbol : ''"
            />
          </v-lazy>

          <!-- Custom Toggle Switch -->

          <banking-switch
            v-model="switchValue"
            :enabled-label="$t('ALM.Section.InvestmentsPortfolio.InvestmentsAllocation.ToggleSwitch.EnabledLabel')"
            :disabled-label="$t('ALM.Section.InvestmentsPortfolio.InvestmentsAllocation.ToggleSwitch.DisabledLabel')"
          />
        </div>
      </v-col>

      <!-- Right Section - Data Table -->
      <v-col
        class="ma-0 pa-2"
        cols="12"
        md="9"
      >
        <div class="">
          <v-row no-gutters>
            <v-col
              class="table-header synthesis-brand-dark-blue--text pa-1 pl-4"
              cols="4"
            >
              {{ $t('ALM.Section.InvestmentsPortfolio.InvestmentsAllocation.TableHeaders.InvestmentType') }}
            </v-col>
            <v-col
              cols="8"
              class="d-flex justify-space-between"
            >
              <div class="table-header synthesis-brand-dark-blue--text pa-1 pl-4">
                {{ $t('ALM.Section.InvestmentsPortfolio.InvestmentsAllocation.TableHeaders.Contribution') }}
              </div>
              <div class="table-header synthesis-brand-dark-blue--text pa-1 pr-4">
                {{ $t('ALM.Section.InvestmentsPortfolio.InvestmentsAllocation.TableHeaders.Notional') }}
              </div>
            </v-col>
          </v-row>

          <v-row
            v-for="(typeAllocation, index) in perTypeAllocationData"

            :key="`alm-investments-portfolio-investments-allocation-legend-${index}`"
            no-gutters
          >
            <v-col
              class=" synthesis-brand-dark-blue--text  pr-1"
              cols="4"
            >
              <item-chart-legend
                :key="`alm-investments-portfolio-investments-allocation-legend-size-${index}`"
                :background-color="themeColorRGBA(getColorByIndex(index), 1)"
                :title="typeAllocation.typeName"
                :title-size="14"
                icon="mdi-circle"
                :icon-color="themeColorRGBA(getIconColorByIndex(index), 1)"
              />
            </v-col>
            <v-col
              cols="8"
            >
              <item-chart-legend
                :key="`alm-investments-portfolio-investments-allocation-legend-size-${index}`"
                :background-color="themeColorRGBA(getColorByIndex(index), 1)"
                :title="`${typeAllocation.contribution ? typeAllocation.contribution.toLocaleString('en-US') : ''}`"
                :title-value="`${typeAllocation.notional ? formatNumberShort(typeAllocation.notional) : ''}`"
                :title-size="14"
                :title-value-size="14"
                :title-value-class="'font-weight-medium d-flex align-center'"
              />
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col
              class=" synthesis-brand-dark-blue--text  pr-1"
              cols="4"
            >
              <item-chart-legend
                :key="`alm-investments-portfolio-investments-allocation-legend-size`"
                :title="totalAllocationData['typeName']"
                :title-size="14"
                :title-class="'font-weight-bold d-flex align-center'"
                icon="mdi-circle"
                :icon-color="themeColorRGBA('synthesis-ui-grey-02', 1)"
                :border="`1px solid ${themeColorRGBA('synthesis-ui-grey-02', 1)}`"
              />
            </v-col>
            <v-col
              cols="8"
            >
              <item-chart-legend
                :key="`alm-investments-portfolio-investments-allocation-legend-size`"
                :title="(totalAllocationData['contribution'] ? totalAllocationData['contribution'].toLocaleString('en-US') : '')"
                :title-size="14"
                :title-class="'font-weight-bold d-flex align-center'"
                :title-value="totalAllocationData['notional'] ? formatNumberShort(totalAllocationData['notional']) : ''"
                :title-value-size="14"
                :title-value-class="'font-weight-bold d-flex align-center'"
                :border="`1px solid ${themeColorRGBA('synthesis-ui-grey-02', 1)}`"
              />
            </v-col>
          </v-row>
        </div>
      </v-col>
    </v-row>
  </block-item-card>
</template>

  <script>
import BlockItemCard from '@/theme/default/components/common/BlockItemCard'
import chartsColorMixin from '@/theme/default/components/charts/mixins/chartsColorMixin'
import { toNumberFormatNotationShort } from '@/lib/utils/helper'
import ItemChartLegend from '@/theme/default/components/common/ItemChartLegend'
import BankingSwitch from '@/theme/default/components/common/BankingSwitch'
import BaseEChartPie from '@/theme/default/components/charts/echarts/BaseEChartPie'

export default {
  name      : 'InvestmentsAllocation',
  components: {
    BlockItemCard,
    ItemChartLegend,
    BankingSwitch,
    BaseEChartPie
  },
  mixins   : [chartsColorMixin],
  props    : {},
  dataStore: {
    Investments: 'ALM.Investments.Data'
  },
  data () {
    return {
      minBlockHeight: 350,
      switchValue   : true

    }
  },
  computed: {
    perTypeAllocationData () {
      return this.Investments?.TypeAllocation?.perTypeAllocationData || []
    },
    currencySymbol () {
      return this.Investments.BaseCurrencyDisplaySymbol || ''
    },
    totalAllocationData () {
      return this.Investments?.TypeAllocation?.totalAllocationData || {}
    },
    typeNames () {
      return this.Investments?.TypeAllocation?.typeNames || []
    },
    investmentsAllocation () {
      const typeAllocation = this.Investments?.TypeAllocation
      if (!typeAllocation?.perTypeAllocationData) return []

      // Transform data for the pie chart, sorting by totalValue in descending order
      const sortedData = [...typeAllocation.perTypeAllocationData]

      return sortedData.map((item, index) => ({
        name     : item.typeName,
        value    : this.switchValue ? item.notional : item.contribution,
        type     : this.switchValue ? 'number' : 'localeNumber',
        itemStyle: {
          color: this.getInvestmentTypeIconColor(index)
        }
      }))
    },

    pieColors () {
      return ['#EA804B', '#57C05B', '#C264DE', '#FFB21E']
    }
  },

  watch: {
    selectedPeriod () {
      this.updateResourcePeriod()
    }
  },
  mounted () {},
  methods: {
    formatNumberShort (value, includeCurrencySymbol = true) {
      return (includeCurrencySymbol ? this.currencySymbol + '' : '') + toNumberFormatNotationShort(value)
    },
    getInvestmentTypeIconColor (index) {
      return ['#EA804B', '#57C05B', '#C264DE', '#FFB21E'][index % 4]
    },
    getColorByIndex (index) {
      switch (index) {
        case 0:
          return 'synthesis-ui-orange-08'
        case 1:
          return 'synthesis-ui-green-08'
        case 2:
          return 'synthesis-ui-purple-08'
        case 3:
          return 'synthesis-ui-yellow-08'
        default:
          return 'synthesis-ui-grey-08'
      }
    },
    getIconColorByIndex (index) {
      switch (index) {
        case 0:
          return 'synthesis-ui-orange-01'
        case 1:
          return 'synthesis-ui-green-01'
        case 2:
          return 'synthesis-ui-purple-01'
        case 3:
          return 'synthesis-ui-yellow-01'
        default:
          return 'synthesis-ui-grey-01'
      }
    }

  }
}
  </script>

  <style scoped>

  .table-header {
  font-size: 12px;
  font-weight: 400;

}
  </style>
